import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import SecurityConfig from "./components/SecurityConfig";
import EmailNotificationsConfig from "./components/EmailNotificationsConfig";
import UserManagementConfig from "./components/UserManagementConfig";
import SystemParametersConfig from "./components/SystemParametersConfig";
import ConfigStatusPanel from "./components/ConfigStatusPanel";
import SaveConfirmModal from "./components/SaveConfirmModal";
import NotificationsEmailConfig from "./components/NotificationsEmailConfig";
import EmailTemplatesConfig from "./components/EmailTemplatesConfig";
import { useAuth } from "../../contexts/AuthContext";

const TABS = [
  { key: "security", label: "Seguridad", icon: "Shield" },
  { key: "email", label: "Notificaciones", icon: "Mail" },
  { key: "notificaciones", label: "Notif. y Correo", icon: "BellRing" },
  { key: "userManagement", label: "Usuarios", icon: "Users" },
  { key: "systemParams", label: "Parámetros", icon: "Settings" },
  { key: "plantillas", label: "Plantillas de Correo", icon: "FileText", superAdminOnly: true },
];

const INITIAL_CONFIG = {
  security: {
    minPasswordLength: "8",
    maxLoginAttempts: "5",
    passwordExpiryDays: "90",
    passwordHistory: "5",
    requireUppercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    requireMFA: false,
    sessionTimeout: "30",
    maxSessionDuration: "8",
    signedUrlExpiry: "24",
    maxConcurrentSessions: "3",
  },
  email: {
    smtpHost: "smtp.softdist.co",
    smtpPort: "587",
    smtpUser: "notificaciones@softdist.co",
    smtpPassword: "••••••••",
    fromEmail: "noreply@softdist.co",
    fromName: "SoftDist Notificaciones",
  },
  userManagement: {
    approvalDays: "3",
    reminderDays: "1",
    autoRejectExpired: true,
    notifyAdminOnRegister: true,
    requireNITValidation: true,
    allowSelfRegistration: true,
  },
  systemParams: {
    maxFileSizeMB: "500",
    maxConcurrentUploads: "3",
    allowedFileTypes: ".zip,.tar.gz,.exe,.msi,.pkg",
    totalStorageGB: "100",
    auditRetentionDays: "365",
    downloadLogRetentionDays: "180",
    securityLogRetentionDays: "730",
    cleanupSchedule: "Diaria a las 02:00 COT",
    strictNITValidation: true,
    allowForeignOrgs: false,
  },
};

export default function SystemConfiguration() {
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState("security");
  const [config, setConfig] = useState(INITIAL_CONFIG);
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleConfigChange = (section, field, value) => {
    setConfig((prev) => ({
      ...prev,
      [section]: { ...prev?.[section], [field]: value },
    }));
    setHasChanges(true);
  };

  const handleSaveClick = () => {
    setShowModal(true);
    setSavedSuccess(false);
  };

  const handleConfirmSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setSavedSuccess(true);
      setHasChanges(false);
      setTimeout(() => {
        setShowModal(false);
        setSavedSuccess(false);
      }, 2000);
    }, 1500);
  };

  const handleCancelModal = () => {
    setShowModal(false);
  };

  const handleLogout = async () => {
    await signOut?.();
    navigate("/login");
  };

  const isSuperAdmin = userProfile?.role === "SUPER_ADMIN";
  const visibleTabs = TABS?.filter((t) => !t?.superAdminOnly || isSuperAdmin);

  const currentUser = {
    name: userProfile?.full_name || 'Administrador',
    role: userProfile?.role || 'SUPER_ADMIN',
    organization: 'Update-vital-Ftech',
  };

  const notifications = {
    pendingApprovals: 3,
    pendingUsers: 2,
    pendingUpdates: 5,
    availableUpdates: 0,
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--color-background)" }}>
      <RoleBasedSidebar
        user={currentUser}
        notifications={notifications}
        onLogout={handleLogout}
      />
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-60">
        {/* Top Header — Vital branded */}
        <header
          className="sticky top-0 z-10 flex items-center justify-between px-4 md:px-6 lg:px-8 py-4"
          style={{ backgroundColor: "var(--color-card)", borderBottom: '2px solid var(--color-primary)', boxShadow: "0 2px 8px rgba(0,188,212,0.1)" }}
        >
          <div className="flex items-center gap-3 ml-12 lg:ml-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#00BCD4" }}>
              <Icon name="Settings" size={18} color="#fff" />
            </div>
            <div>
              <h1 className="font-heading font-700 text-base md:text-lg leading-tight" style={{ color: "var(--color-foreground)" }}>
                Configuración del Sistema
              </h1>
              <p className="text-xs font-caption hidden sm:block" style={{ color: "var(--color-primary)" }}>
                Parámetros globales de la plataforma SoftDist
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-caption" style={{ backgroundColor: "rgba(217,119,6,0.12)", color: "var(--color-warning)" }}>
                <Icon name="AlertCircle" size={12} color="var(--color-warning)" />
                Cambios sin guardar
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              className="hidden md:flex"
              style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
            >
              Exportar
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Save"
              iconPosition="left"
              onClick={handleSaveClick}
              disabled={!hasChanges}
              style={{ backgroundColor: '#00BCD4', borderColor: '#00BCD4' }}
            >
              <span className="hidden sm:inline">Guardar cambios</span>
              <span className="sm:hidden">Guardar</span>
            </Button>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left: Tabs + Config */}
              <div className="flex-1 min-w-0">
                {/* Tab Navigation */}
                <div className="flex overflow-x-auto gap-1 mb-6 p-1 rounded-xl" style={{ backgroundColor: "var(--color-muted)" }}>
                  {visibleTabs?.map((tab) => (
                    <button
                      key={tab?.key}
                      onClick={() => setActiveTab(tab?.key)}
                      className="flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-lg text-sm font-caption font-500 transition-base flex-shrink-0 whitespace-nowrap"
                      style={{
                        backgroundColor: activeTab === tab?.key ? "var(--color-card)" : "transparent",
                        color: activeTab === tab?.key ? "var(--color-primary)" : "var(--color-muted-foreground)",
                        boxShadow: activeTab === tab?.key ? "var(--shadow-sm)" : "none",
                      }}
                    >
                      <Icon
                        name={tab?.icon}
                        size={15}
                        color={activeTab === tab?.key ? "var(--color-primary)" : "currentColor"}
                      />
                      {tab?.label}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                {activeTab === "security" && (
                  <SecurityConfig
                    config={config?.security}
                    onChange={handleConfigChange}
                    onTest={() => {}}
                  />
                )}
                {activeTab === "email" && (
                  <EmailNotificationsConfig
                    config={config?.email}
                    onChange={handleConfigChange}
                  />
                )}
                {activeTab === "userManagement" && (
                  <UserManagementConfig
                    config={config?.userManagement}
                    onChange={handleConfigChange}
                  />
                )}
                {activeTab === "systemParams" && (
                  <SystemParametersConfig
                    config={config?.systemParams}
                    onChange={handleConfigChange}
                  />
                )}
                {activeTab === "notificaciones" && (
                  <NotificationsEmailConfig onToast={showToast} />
                )}
                {activeTab === "plantillas" && isSuperAdmin && (
                  <EmailTemplatesConfig onToast={showToast} />
                )}
              </div>

              {/* Right: Status Panel */}
              <div className="w-full lg:w-72 xl:w-80 flex-shrink-0">
                <ConfigStatusPanel />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="px-4 md:px-6 lg:px-8 py-4 border-t text-center" style={{ borderColor: "var(--color-border)" }}>
          <p className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>
            &copy; {new Date()?.getFullYear()} SoftDist S.A.S. &mdash; Distribución controlada de software para empresas colombianas
          </p>
        </footer>
      </div>
      {/* Save Confirmation Modal */}
      <SaveConfirmModal
        isOpen={showModal}
        onConfirm={handleConfirmSave}
        onCancel={handleCancelModal}
        isSaving={isSaving}
        savedSuccess={savedSuccess}
      />
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-[300] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-caption max-w-sm transition-all ${toast?.type === 'error' ? 'bg-red-600' : 'bg-emerald-600'}`}>
          <Icon name={toast?.type === 'error' ? 'AlertTriangle' : 'CheckCircle'} size={16} color="white" />
          {toast?.message}
          <button onClick={() => setToast(null)} className="ml-auto"><Icon name="X" size={14} color="white" /></button>
        </div>
      )}
    </div>
  );
}